import { z } from 'zod';

// The London Life team chat (Lagos Life's "Lagos Life team": pinned at the top of Messages). Only the team
// posts: announcements, and polls with one vote each that close at a set time. Players read and vote; the
// unread count adds to the Phone's badge.

export const TEAM_POST_TITLE_MAX = 80;
export const TEAM_POST_BODY_MAX = 600;
export const TEAM_POLL_OPTIONS_MAX = 8;
export const TEAM_POLL_HOURS_MAX = 24 * 14;
export const TEAM_PAGE = 30;

export const TeamPostKind = z.enum(['announcement', 'poll']);
export type TeamPostKind = z.infer<typeof TeamPostKind>;

export const TeamPostView = z.object({
  id: z.string().uuid(),
  kind: TeamPostKind,
  title: z.string(),
  body: z.string(),
  createdAt: z.string(),
  // polls only
  closesAt: z.string().nullable(),
  options: z.array(z.object({ id: z.string(), label: z.string(), votes: z.number().int().nullable() })),
  // what I chose; counts show once I have voted or the poll has closed
  myVote: z.string().nullable(),
  totalVotes: z.number().int().nullable(),
});
export type TeamPostView = z.infer<typeof TeamPostView>;

export const TeamReply = z.object({ posts: z.array(TeamPostView), unread: z.number().int() });
export type TeamReply = z.infer<typeof TeamReply>;

export const TeamVote = z.object({ optionId: z.string().min(1).max(40) }).strict();
export type TeamVote = z.infer<typeof TeamVote>;
export const TeamVoteReply = z.object({ post: TeamPostView, message: z.string() });
export type TeamVoteReply = z.infer<typeof TeamVoteReply>;

// the team writes a post (Admin → Players → Team chat)
export const TeamPostCreate = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('announcement'),
      title: z.string().trim().min(3).max(TEAM_POST_TITLE_MAX),
      body: z.string().trim().min(3).max(TEAM_POST_BODY_MAX),
    })
    .strict(),
  z
    .object({
      kind: z.literal('poll'),
      title: z.string().trim().min(3).max(TEAM_POST_TITLE_MAX),
      body: z.string().trim().max(TEAM_POST_BODY_MAX).default(''),
      options: z
        .array(z.string().trim().min(1).max(60))
        .min(2)
        .max(TEAM_POLL_OPTIONS_MAX)
        .refine(
          (options) => new Set(options.map((option) => option.toLowerCase())).size === options.length,
          'Each choice once',
        ),
      hours: z.number().int().min(1).max(TEAM_POLL_HOURS_MAX),
    })
    .strict(),
]);
export type TeamPostCreate = z.infer<typeof TeamPostCreate>;

// what the admin page shows: every post with full counts
export const TeamPostAdminView = TeamPostView.extend({
  createdBy: z.string().nullable(),
  removedAt: z.string().nullable(),
});
export type TeamPostAdminView = z.infer<typeof TeamPostAdminView>;
export const TeamAdminReply = z.object({ posts: z.array(TeamPostAdminView) });
export type TeamAdminReply = z.infer<typeof TeamAdminReply>;
